import type { ReactNode } from 'react';
import { formatValue } from './fieldChanges';

export function JsonBlock({ value, label }: { value: unknown; label: string }) {
  return (
    <figure className="mt-2">
      <figcaption className="eyebrow">{label}</figcaption>
      <pre className="mt-1 max-h-64 overflow-auto rounded border border-line bg-surface-2 p-2 font-mono text-xs text-ink">
        {JSON.stringify(value, null, 2)}
      </pre>
    </figure>
  );
}

/** Each field a PATCH changes, before and after, then the body that is sent. */
export function FieldDiff({
  before,
  patch,
  children,
}: {
  before: Record<string, unknown>;
  patch: Record<string, unknown>;
  children?: ReactNode;
}) {
  return (
    <>
      <table className="mt-2 w-full text-left">
        <thead className="eyebrow">
          <tr>
            <th className="py-1 pr-3 font-semibold">Field</th>
            <th className="px-2 font-semibold">Before</th>
            <th className="px-2 font-semibold">After</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(patch).map(([field, value]) => (
            <tr key={field} className="border-t border-line align-top">
              <td className="py-1 pr-3 font-mono text-ink">{field}</td>
              <td className="break-all px-2 font-mono">
                {formatValue(before[field])}
              </td>
              <td className="break-all px-2 font-mono text-ink">
                {formatValue(value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {children}
      <JsonBlock value={patch} label="PATCH body" />
    </>
  );
}

/** Every field of a record, objects as JSON, for an expanded table row. */
export function FieldList({ record }: { record: Record<string, unknown> }) {
  return (
    <dl className="grid grid-cols-[minmax(10rem,max-content)_minmax(0,1fr)] gap-x-4 gap-y-0.5">
      {Object.entries(record).map(([key, value]) => (
        <div key={key} className="contents">
          <dt className="font-mono text-muted">{key}</dt>
          <dd className="break-all font-mono text-ink">{formatValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
}
